import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import PlanSelector, { PublicPlan } from '@/components/PlanSelector';
import SignupDialog from '@/components/SignupDialog';

export default function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightKey = searchParams.get('plan');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<PublicPlan | null>(null);

  const startCheckout = async (workspaceId: string, planKey: string) => {
    setBusyKey(planKey);
    try {
      const res = await apiFetch<{ checkoutUrl: string }>(`/workspaces/${workspaceId}/billing/checkout`, {
        method: 'POST',
        body: JSON.stringify({ planKey, topUpCredits: 0, acceptTerms: true }),
      });
      window.location.href = res.checkoutUrl;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Checkout failed');
      setBusyKey(null);
    }
  };

  // For an already-signed-in user: use their workspace (or create one if missing).
  const resolveWorkspaceId = async (): Promise<string> => {
    const stored = localStorage.getItem('workspaceId');
    if (stored) return stored;
    const { workspaces } = await apiFetch<{ workspaces: { id: string }[] }>('/workspaces');
    if (workspaces && workspaces.length > 0) {
      localStorage.setItem('workspaceId', workspaces[0].id);
      return workspaces[0].id;
    }
    const created = await apiFetch<{ workspace: { id: string } }>('/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name: 'My Workspace' }),
    });
    localStorage.setItem('workspaceId', created.workspace.id);
    return created.workspace.id;
  };

  const handleSubscribe = async (plan: PublicPlan) => {
    if (plan.price == null) { navigate('/support'); return; }

    // Not signed in -> show the signup popup (account + workspace), then pay.
    if (!localStorage.getItem('token')) {
      setPendingPlan(plan);
      setAuthOpen(true);
      return;
    }

    try {
      setBusyKey(plan.key);
      const workspaceId = await resolveWorkspaceId();
      await startCheckout(workspaceId, plan.key);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start checkout');
      setBusyKey(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-3">{t('checkout.choose_plan', 'Choose your plan')}</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('checkout.choose_plan_subtitle', 'Select a plan and pay securely with Stripe. Change or cancel anytime.')}
            </p>
          </div>
          <PlanSelector highlightKey={highlightKey} busyKey={busyKey} ctaLabel="Subscribe" onSubscribe={handleSubscribe} />
        </div>
      </main>
      <Footer />

      <SignupDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        onComplete={(workspaceId) => {
          setAuthOpen(false);
          if (pendingPlan) startCheckout(workspaceId, pendingPlan.key);
        }}
      />
    </div>
  );
}
